import type { ClassifyResult, LabelingSession, LabelingSessionImage, ImageLabel, LabelingExperiment, LabelingExperimentLabels } from '../types/LabelingTypes';
import type { HttpClient } from '@/lib/http/HttpClient';

import * as mockImpl from './LabelingApiClient.mock';
import * as realImpl from './LabelingApiClient.real';

// Real backend by default: the experiment/session/image flow is wired to the backend.
// Set VITE_LABELIZE_USE_MOCK=true to use mock data instead.
// NOTE: labelizeClassify / labelizeClassifyBatch call the label_prediction CNN worker
// in the real client and will throw — that is a separate feature, not the image-loading flow.
const impl = import.meta.env.VITE_LABELIZE_USE_MOCK === 'true' ? mockImpl : realImpl;

export function labelizeClassify(http: HttpClient, key: string): Promise<ClassifyResult> {
  return impl.labelizeClassify(http, key);
}

export function labelizeClassifyBatch(http: HttpClient, keys: string[]): Promise<ClassifyResult[]> {
  return impl.labelizeClassifyBatch(http, keys);
}

export function labelizeListSessions(
  http: HttpClient,
  toolId: string,
  signal?: AbortSignal
): Promise<LabelingSession[]> {
  return impl.labelizeListSessions(http, toolId, signal);
}

export function labelizeCreateSession(
  http: HttpClient,
  toolId: string,
  signal?: AbortSignal
): Promise<LabelingSession> {
  return impl.labelizeCreateSession(http, toolId, signal);
}

export function labelizeListSessionImages(
  http: HttpClient,
  sessionId: string,
  signal?: AbortSignal
): Promise<LabelingSessionImage[]> {
  return impl.labelizeListSessionImages(http, sessionId, signal);
}

export function labelizeListExperimentLabels(
  http: HttpClient,
  experimentId: string,
  signal?: AbortSignal
): Promise<LabelingExperimentLabels> {
  return impl.labelizeListExperimentLabels(http, experimentId, signal);
}

export function labelizeGetSignedImageUrl(
  http: HttpClient,
  experimentId: string,
  partId: string,
  operationId: string,
  imageKey: string,
  signal?: AbortSignal
): Promise<string> {
  return impl.labelizeGetSignedImageUrl(http, experimentId, partId, operationId, imageKey, signal);
}

export function labelizeSaveLabels(
  http: HttpClient,
  sessionId: string,
  labels: Record<string, ImageLabel | null>,
  signal?: AbortSignal
): Promise<{ sessionId: string; updatedCount: number }> {
  return impl.labelizeSaveLabels(http, sessionId, labels, signal);
}

export function labelizeDeleteLabel(
  http: HttpClient,
  sessionId: string,
  imageKey: string,
  signal?: AbortSignal
): Promise<{ success: boolean }> {
  return impl.labelizeDeleteLabel(http, sessionId, imageKey, signal);
}

export function labelizeListExperiments(
  http: HttpClient,
  signal?: AbortSignal
): Promise<LabelingExperiment[]> {
  return impl.labelizeListExperiments(http, signal);
}

export type { StoredPrediction } from './LabelingApiClient.real';

export function labelizeListPredictions(
  http: HttpClient,
  experimentId: string,
  signal?: AbortSignal
) {
  return impl.labelizeListPredictions(http, experimentId, signal);
}
